import type { CaseCptLine, SurgeonTemplate } from '@/lib/templates/types';
import { parseCptCsv } from '@/lib/import/cptCsvParser';
import { getProofMap, ORTHOPEDIC_PROOF_MAPS } from '@/lib/proofmap/orthopedicRules';
import cptBundleCsv from '../../../public/link_cpt_2.csv?raw';

export type CptSearchSource =
  | 'cpt_catalog'
  | 'procedure'
  | 'authorized'
  | 'template'
  | 'bundle'
  | 'proof_map'
  | 'known';

export interface CptSearchEntry {
  code: string;
  description: string;
  category?: string | null;
  source: CptSearchSource;
  priority: number;
  preAuthorized?: boolean;
  plausible?: boolean;
  ncciSupported?: boolean;
  ncciReason?: string;
  modifierAllowed?: boolean;
  reviewRequired?: boolean;
  notes?: string;
  defaultModifiers?: string[];
}

export interface CptSearchContext {
  authorizedLines?: CaseCptLine[];
  template?: SurgeonTemplate | null;
  primaryCpt?: string | null;
  remoteEntries?: CptSearchEntry[];
}

export interface CptSearchOptions {
  limit?: number;
  existingCodes?: Iterable<string>;
}

export const KNOWN_CPT_DESCRIPTIONS: Record<string, string> = {
  '20610': 'Major joint injection or aspiration',
  '20680': 'Removal of deep implant or hardware',
  '25115': 'Radical excision of wrist/forearm synovium or bursa',
  '23120': 'Distal clavicle resection/acromioclavicular joint excision',
  '23410': 'Open repair of acute rotator cuff tear',
  '23412': 'Open repair of chronic rotator cuff tear',
  '23420': 'Open rotator cuff repair with acromioplasty',
  '23430': 'Open biceps tenodesis',
  '23470': 'Shoulder hemiarthroplasty',
  '23472': 'Total shoulder arthroplasty',
  '27125': 'Partial hip replacement',
  '27130': 'Total hip arthroplasty',
  '27132': 'Conversion hip arthroplasty',
  '27134': 'Revision total hip arthroplasty',
  '27137': 'Revision acetabular component',
  '27310': 'Knee arthrotomy with exploration or removal of loose/foreign body',
  '27331': 'Knee synovectomy or synovial excision',
  '27385': 'Suture of quadriceps or hamstring muscle rupture; primary',
  '27386': 'Suture of quadriceps or hamstring muscle rupture; secondary reconstruction',
  '27435': 'Knee capsular release for contracture',
  '27438': 'Patellar realignment or resurfacing procedure',
  '27447': 'Total knee arthroplasty',
  '27486': 'Revision total knee arthroplasty, one component',
  '27487': 'Revision total knee arthroplasty, femoral and tibial components',
  '27570': 'Manipulation of knee joint under general anesthesia',
  '29806': 'Shoulder arthroscopy with capsulorrhaphy',
  '29807': 'Shoulder arthroscopy with SLAP repair',
  '29822': 'Shoulder arthroscopy with limited debridement',
  '29823': 'Shoulder arthroscopy with extensive debridement',
  '29824': 'Shoulder arthroscopy with distal clavicle excision',
  '29826': 'Shoulder arthroscopy with subacromial decompression',
  '29827': 'Shoulder arthroscopy with rotator cuff repair',
  '29828': 'Shoulder arthroscopy with biceps tenodesis',
  '29875': 'Knee arthroscopy with limited synovectomy',
  '29876': 'Knee arthroscopy with major synovectomy',
  '29877': 'Knee arthroscopy with chondroplasty',
  '29874': 'Knee arthroscopy with loose body removal',
  '29879': 'Knee arthroscopy with abrasion arthroplasty or microfracture',
  '29880': 'Knee arthroscopy with medial and lateral meniscectomy',
  '29881': 'Knee arthroscopy with meniscectomy',
  '29882': 'Knee arthroscopy with meniscus repair',
  '63030': 'Lumbar laminotomy with decompression/discectomy',
  '63035': 'Lumbar laminotomy with decompression at additional level',
  '63047': 'Lumbar laminectomy/facetectomy/foraminotomy',
  '63048': 'Lumbar laminectomy/facetectomy/foraminotomy, each additional segment',
  '64708': 'Neuroplasty of major peripheral nerve, arm or leg',
  '64718': 'Neuroplasty or transposition of ulnar nerve at elbow',
  '64719': 'Neuroplasty or transposition of ulnar nerve at wrist',
  '64721': 'Carpal tunnel release with median neuroplasty',
  '26145': 'Synovectomy of flexor tendon sheath, hand or finger',
  '20526': 'Therapeutic injection, median nerve at wrist',
};

const OFFICIAL_SOURCE_PRIORITY: Partial<Record<CptSearchSource, number>> = {
  cpt_catalog: 3,
  procedure: 3,
  authorized: 2,
  template: 2,
  proof_map: 1,
  known: 0,
  bundle: 0,
};

export function normalizeCptCode(code: string | number | null | undefined): string {
  return String(code || '').trim().toUpperCase();
}

function normalizeText(value: string | null | undefined): string {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

export function descriptionForCode(code: string, fallback?: string | null): string {
  const normalizedCode = normalizeCptCode(code);
  return fallback?.trim()
    || getProofMap(normalizedCode)?.cpt_description
    || KNOWN_CPT_DESCRIPTIONS[normalizedCode]
    || `CPT ${normalizedCode}`;
}

function normalizeEntry(entry: CptSearchEntry): CptSearchEntry | null {
  const code = normalizeCptCode(entry.code);
  if (!code) return null;

  return {
    ...entry,
    code,
    description: descriptionForCode(code, entry.description),
    category: entry.category ?? null,
    defaultModifiers: Array.from(new Set(entry.defaultModifiers || [])),
    preAuthorized: Boolean(entry.preAuthorized),
    plausible: Boolean(entry.plausible),
    ncciSupported: Boolean(entry.ncciSupported),
    ncciReason: entry.ncciReason,
    modifierAllowed: entry.modifierAllowed,
    reviewRequired: Boolean(entry.reviewRequired),
  };
}

function isBetterDescription(next: CptSearchEntry, current: CptSearchEntry): boolean {
  const currentIsPlaceholder = current.description === `CPT ${current.code}`;
  const nextSourceWeight = OFFICIAL_SOURCE_PRIORITY[next.source] ?? 0;
  const currentSourceWeight = OFFICIAL_SOURCE_PRIORITY[current.source] ?? 0;

  if (currentIsPlaceholder && next.description !== `CPT ${next.code}`) return true;
  if (nextSourceWeight > currentSourceWeight && next.description.trim()) return true;
  return false;
}

export function mergeCptSearchEntries(entries: CptSearchEntry[]): CptSearchEntry[] {
  const merged = new Map<string, CptSearchEntry>();

  entries.forEach((rawEntry) => {
    const entry = normalizeEntry(rawEntry);
    if (!entry) return;

    const existing = merged.get(entry.code);
    if (!existing) {
      merged.set(entry.code, entry);
      return;
    }

    const priority = Math.max(existing.priority, entry.priority);
    const preferredBase = entry.priority > existing.priority ? entry : existing;
    const description = isBetterDescription(entry, existing) ? entry.description : existing.description;

    merged.set(entry.code, {
      ...preferredBase,
      code: existing.code,
      description,
      category: entry.category || existing.category,
      source: entry.priority > existing.priority ? entry.source : existing.source,
      priority,
      preAuthorized: existing.preAuthorized || entry.preAuthorized,
      plausible: existing.plausible || entry.plausible,
      ncciSupported: existing.ncciSupported || entry.ncciSupported,
      ncciReason: entry.ncciReason || existing.ncciReason,
      modifierAllowed: existing.modifierAllowed || entry.modifierAllowed,
      reviewRequired: existing.reviewRequired && entry.reviewRequired,
      notes: [existing.notes, entry.notes].filter(Boolean).join(' '),
      defaultModifiers: Array.from(new Set([
        ...(existing.defaultModifiers || []),
        ...(entry.defaultModifiers || []),
      ])),
    });
  });

  return Array.from(merged.values()).sort(compareCatalogEntries);
}

function compareCatalogEntries(a: CptSearchEntry, b: CptSearchEntry): number {
  if (a.preAuthorized !== b.preAuthorized) return a.preAuthorized ? -1 : 1;
  if (a.plausible !== b.plausible) return a.plausible ? -1 : 1;
  if (a.priority !== b.priority) return b.priority - a.priority;
  return a.code.localeCompare(b.code);
}

export function buildLocalCptSearchCatalog(context: CptSearchContext = {}): CptSearchEntry[] {
  const entries: CptSearchEntry[] = [];
  const authorizedLines = context.authorizedLines || [];
  const authorizedCodes = new Set(authorizedLines.map(line => normalizeCptCode(line.code)));
  const primaryCpt = normalizeCptCode(context.primaryCpt || authorizedLines[0]?.code);

  authorizedLines.forEach((line, index) => {
    entries.push({
      code: line.code,
      description: line.description,
      source: 'authorized',
      priority: 100 - index,
      preAuthorized: true,
      plausible: true,
      defaultModifiers: line.modifiers,
    });
  });

  const templateLines = [
    ...(context.template?.primary_cpts || []),
    ...(context.template?.secondary_cpts || []),
    ...(context.template?.common_addons || []),
  ];
  templateLines.forEach((line, index) => {
    const code = normalizeCptCode(line.code);
    entries.push({
      code,
      description: line.description,
      source: 'template',
      priority: 88 - index,
      preAuthorized: authorizedCodes.has(code),
      plausible: true,
      defaultModifiers: 'default_modifiers' in line ? line.default_modifiers || [] : [],
    });
  });

  const parsedBundle = parseCptCsv(cptBundleCsv);
  parsedBundle.rows.forEach((row, rowIndex) => {
    const bundleCodes = [row.primary_cpt, ...row.subset_cpts].map(normalizeCptCode);
    const rowMatchesPrimary = primaryCpt ? bundleCodes.includes(primaryCpt) : false;

    bundleCodes.forEach((code, codeIndex) => {
      entries.push({
        code,
        description: descriptionForCode(code),
        source: 'bundle',
        priority: rowMatchesPrimary ? 76 - codeIndex : 42 - rowIndex / 100,
        preAuthorized: authorizedCodes.has(code),
        plausible: rowMatchesPrimary,
        notes: row.notes,
        defaultModifiers: [],
      });
    });
  });

  ORTHOPEDIC_PROOF_MAPS.forEach((proofMap, index) => {
    entries.push({
      code: proofMap.cpt_code,
      description: proofMap.cpt_description,
      source: 'proof_map',
      priority: 58 - index / 100,
      preAuthorized: authorizedCodes.has(proofMap.cpt_code),
      plausible: proofMap.cpt_code === primaryCpt,
      defaultModifiers: [],
    });
  });

  Object.entries(KNOWN_CPT_DESCRIPTIONS).forEach(([code, description], index) => {
    entries.push({
      code,
      description,
      source: 'known',
      priority: 24 - index / 100,
      preAuthorized: authorizedCodes.has(code),
      plausible: false,
      defaultModifiers: [],
    });
  });

  return mergeCptSearchEntries(entries);
}

export function buildCptSearchCatalog(context: CptSearchContext = {}): CptSearchEntry[] {
  return mergeCptSearchEntries([
    ...buildLocalCptSearchCatalog(context),
    ...(context.remoteEntries || []),
  ]);
}

function scoreEntry(entry: CptSearchEntry, normalizedQuery: string): number | null {
  if (!normalizedQuery) return entry.priority;

  const code = normalizeText(entry.code);
  const description = normalizeText(entry.description);
  const category = normalizeText(entry.category || '');
  const notes = normalizeText(entry.notes || '');
  const searchable = [code, description, category, notes].filter(Boolean).join(' ');
  const terms = normalizedQuery.split(/\s+/).filter(Boolean);

  let score: number | null = null;
  if (code === normalizedQuery) score = 1200;
  else if (code.startsWith(normalizedQuery)) score = 950;
  else if (code.includes(normalizedQuery)) score = 720;
  else if (description.includes(normalizedQuery)) score = 620;
  else if (notes.includes(normalizedQuery)) score = 360;
  else if (terms.length > 1 && terms.every(term => searchable.includes(term))) {
    score = 520 + terms.length * 12;
  } else if (terms.length === 1 && searchable.includes(terms[0])) {
    score = 420;
  }

  if (score === null) return null;

  return score
    + entry.priority
    + (entry.preAuthorized ? 90 : 0)
    + (entry.ncciSupported ? 75 : 0)
    + (entry.plausible ? 55 : 0)
    - (entry.reviewRequired ? 35 : 0);
}

export function searchCptCatalog(
  catalog: CptSearchEntry[],
  query: string,
  options: CptSearchOptions = {},
): CptSearchEntry[] {
  const normalizedQuery = normalizeText(query);
  const existingCodes = new Set(Array.from(options.existingCodes || []).map(normalizeCptCode));
  const limit = options.limit ?? 12;

  return catalog
    .filter(entry => !existingCodes.has(entry.code))
    .map((entry) => {
      const score = scoreEntry(entry, normalizedQuery);
      return score === null ? null : { entry, score };
    })
    .filter((item): item is { entry: CptSearchEntry; score: number } => Boolean(item))
    .sort((a, b) => b.score - a.score || compareCatalogEntries(a.entry, b.entry))
    .slice(0, limit)
    .map(item => item.entry);
}
